'use client'

import { useTranslations } from 'next-intl'
import { ComponentType, PropsWithChildren, SVGProps } from 'react'

import {
  FastVacancyResolution,
  ConvenientManagement,
  SafeRental,
} from '@/shared/asset'
import { useRouter } from '@/shared/i18n'
import { Button, Dialog } from '@/shared/ui'

type Content = {
  title: string
  description: string
  image: ComponentType<SVGProps<SVGSVGElement>>
}

export const RoomRegistrationDialogTrigger = ({
  children,
}: PropsWithChildren) => {
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <RoomRegistrationDialog />
    </Dialog.Root>
  )
}

const RoomRegistrationDialog = () => {
  const t = useTranslations('unit_listing')
  const router = useRouter()

  const content: Content[] = [
    {
      title: t('benefit_fast_vacancy_solution.title'),
      description: t('benefit_fast_vacancy_solution_description'),
      image: FastVacancyResolution,
    },
    {
      title: t('benefit_easy_management.title'),
      description: t('benefit_easy_management_description'),
      image: ConvenientManagement,
    },
    {
      title: t('benefit_safe_rental.title'),
      description: t('benefit_safe_rental_description'),
      image: SafeRental,
    },
  ]

  const handleClickButton = () => {
    router.push('/host/main')
  }

  return (
    <Dialog.Content className="p-0">
      <Dialog.Header className="p-6 pb-0">
        <Dialog.Title>{t('header')}</Dialog.Title>
      </Dialog.Header>
      <Dialog.Body className="-mb-3 flex flex-col gap-6 pb-3">
        <h3 className="typo-body1 px-6 font-semibold whitespace-break-spaces text-black">
          {t('title')}
        </h3>
        <ul className="space-y-3 px-5">
          {content.map(({ title, description, image: SVGImage }, index) => (
            <li key={index}>
              <div className="flex items-center gap-10 rounded-sm px-7.5 py-5 shadow-[1px_1px_15px_0_rgba(0,0,0,0.1)]">
                <SVGImage width={58} height={58} className="shrink-0" />
                <div>
                  <h4 className="typo-body1 mb-2 font-semibold text-black">
                    {title}
                  </h4>
                  <p className="typo-caption1 font-normal whitespace-break-spaces text-gray-600">
                    {description}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </Dialog.Body>
      <Dialog.Footer className="p-6 pt-0">
        <Dialog.Close asChild>
          <Button size="large" color="primary" onClick={handleClickButton}>
            {t('button')}
          </Button>
        </Dialog.Close>
      </Dialog.Footer>
    </Dialog.Content>
  )
}
